import {
  Card,
  CardContent,
  CardHeader,
  Container,
  TextField,
  Unstable_Grid2 as Grid,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Skeleton,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { EditSudent, ShowProfile } from "../../Services/Student";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import Swal from "sweetalert2";
import { Alert as Notif } from "utils/alerts";
import { useTranslation } from "react-i18next";

const Profile = () => {
  const [document, setDocument] = useState(null);
  const [isForeign, setIsForeign] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { idstruser } = useSelector((state) => state["Admin"].userInfo || {});
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
    setValue,
    getValues,
    clearErrors,
    setError,
  } = useForm({
    mode: "onSubmit",
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await ShowProfile();
        const profileData = response?.data || {};

        if (profileData) {
          setValue("fullname", profileData.fullname || "");
          setValue("email", profileData.email || "");
          setValue("address", profileData.address || "");
          setValue("cardnumber", profileData.cardnumber || "");

          const isForeignValue = profileData.isForeignnationals;
          setIsForeign(
            isForeignValue === true ||
            isForeignValue === "true" ||
            isForeignValue === 1
          );
          setIsForeign(isForeignValue);
          setValue("nationalcode", profileData.nationalcode || "");

        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [setValue, navigate]);

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    const validTypes = [
      "application/pdf",
      "application/msword",
      "image/jpeg",
      "image/png",
    ];

    if (!validTypes.includes(file?.type)) {
      setError("document", {
        type: "manual",
        message: "نوع فایل پشتیبانی نمی‌شود.",
      });
      return;
    }

    if (file?.size > 5 * 1024 * 1024) {
      setError("document", {
        type: "manual",
        message: "حجم فایل نباید بیشتر از ۵ مگابایت باشد.",
      });
      return;
    }

    setDocument(file);
    clearErrors("document");
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    maxFiles: 1,
  });

  const onSubmit = async (data) => {
    const confirmResult = await Swal.fire({
      title: t("Profile.alertTitle"),
      text: t("Profile.alert"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("alert.confirmButton"),
      cancelButtonText: t("newFile.editDialogCancel"),
    });

    if (!confirmResult.isConfirmed) {
      return; // Exit if user cancels
    }

    try {
      const formData = new FormData();
      formData.append("idstruser", idstruser);
      formData.append("fullname", data.fullname);
      formData.append("nationalcode", isForeign ? "" : data.nationalcode || "");
      formData.append("email", data.email);
      formData.append("address", data.address);
      formData.append("cardnumber", data.cardnumber);
      formData.append("nationalcode", isForeign ? "" : data.nationalcode || "");
      if (document) {
        formData.append("file", document);
      }
      await EditSudent(formData);
      navigate("/");
    } catch (error) {
      console.error("Error submitting form:", error);
      Notif("خطا", error?.response?.data);

    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardHeader>
            <Skeleton variant="text" width={200} height={40} animation="wave" />
          </CardHeader>
          <CardContent>
            <Grid container spacing={3}>
              <Grid xs={12}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={60}
                  animation="wave"
                />
              </Grid>
              <Grid xs={12} md={6}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={56}
                  animation="wave"
                />
              </Grid>
              <Grid xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Skeleton
                    variant="rectangular"
                    width="80%"
                    height={56}
                    animation="wave"
                  />
                  <Skeleton
                    variant="rectangular"
                    width="20%"
                    height={40}
                    animation="wave"
                  />
                </Box>
              </Grid>
              <Grid xs={12} md={6}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={56}
                  animation="wave"
                />
              </Grid>
              <Grid xs={12}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={100}
                  animation="wave"
                />
              </Grid>
              <Grid xs={12}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={120}
                  animation="wave"
                />
              </Grid>
              <Grid xs={12}>
                <Skeleton
                  variant="rectangular"
                  width={120}
                  height={40}
                  animation="wave"
                  sx={{ ml: "auto" }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <CardHeader
          title={t("Profile.title")}
          titleTypographyProps={{ variant: "h5", fontWeight: "bold" }}
          sx={{ pb: 1 }}
        />
        <CardContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <Grid container spacing={3}>
              <Grid xs={12} marginBottom={1}>
                <Alert
                  variant="filled"
                  severity="warning"
                  sx={{
                    mb: 3,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "clip",
                    maxWidth: "100%",
                    p: 1,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflow: "hidden",
                      textOverflow: "clip",
                      maxWidth: "100%",
                      lineHeight: 1.7,
                    }}
                  >
                 {t("Profile.alert")}
                  </Typography>
                </Alert>
              </Grid>
              <Grid xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "start",
                    gap: 2,
                    flexDirection: "column",
                  }}
                >
                  <TextField
                    fullWidth
                    label= {t("Profile.name")}
                    required
                    variant="outlined"
                    error={!!formErrors.fullname}
                    InputLabelProps={{
                      shrink: getValues().fullname ? true : false,
                    }}
                    helperText={formErrors.fullname?.message?.toString() ?? ""}
                    {...register("fullname", {
                      required: t("Profile.fullnameRequired"),
                    })}
                  />
                </Box>
              </Grid>

              <Grid xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "start",
                    gap: 0,
                    flexDirection: "column",
                  }}
                >
                  <TextField
                    fullWidth
                    type="number"
                    label= {t("Profile.nationalcode")}
                    InputLabelProps={{
                      shrink: getValues().nationalcode ? true : false,
                    }}
                    variant="outlined"
                    error={!!formErrors.nationalcode}
                    helperText={
                      formErrors.nationalcode?.message?.toString() ?? ""
                    }
                    disabled={isForeign}
                    {...register("nationalcode", {
                      validate: (value) => {
                        if (!isForeign && !value) return t("Profile.nationalcodeRequired");
                        if (!isForeign && !/^[\d]{10}$/.test(value))
                          return t("Profile.nationalcodePattern");
                        return true;
                      },
                    })}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isForeign}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsForeign(checked);
                          setValue("nationalcode", "");
                          if (checked) clearErrors("nationalcode");
                        }}
                      />
                    }
                    label= {t("Profile.foreign")}
                  />
                </Box>
              </Grid>

              <Grid xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  dir="ltr"
                  label={t("Profile.email")}
                  variant="outlined"
                  {...register("email", {
                    required: t("Profile.emailRequired"),
                  })}
                  error={!!formErrors.email}
                  helperText={formErrors.email?.message?.toString() ?? ""}
                  InputLabelProps={{ shrink: Boolean(getValues("email")) }}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <TextField
                  fullWidth
                  dir="ltr"
                  label={t("Profile.card")}
                  InputLabelProps={{
                    shrink: getValues().cardnumber ? true : false,
                  }}
                  variant="outlined"
                  error={!!formErrors.cardnumber}
                  helperText={formErrors.cardnumber?.message?.toString() ?? ""}
                  {...register("cardnumber", {
                    required: t("Profile.cardnumberRequired"),
                  })}
                />
              </Grid>
              <Grid xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  dir="rtl"
                  label={t("Profile.address")}
                  InputLabelProps={{
                    shrink: getValues().address ? true : false,
                  }}
                  variant="outlined"
                  error={!!formErrors.address}
                  helperText={formErrors.address?.message?.toString() ?? ""}
                  {...register("address", {
                    required: t("Profile.addressRequired"),
                    maxLength: {
                      value: 500,
                      message: t("Profile.addressMax"),
                    },
                  })}
                />
              </Grid>

              <Grid xs={12}>
                <Box
                  {...getRootProps()}
                  sx={{
                    border: "2px dashed",
                    borderColor: isDragActive ? "primary.main" : "grey.400",
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    bgcolor: isDragActive ? "grey.100" : "white",
                    height: "120px",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 48 }} />
                  <Typography variant="body1" color="text.primary">
                    {isDragActive
                      ? t("Profile.drop")
                      : document
                      ? t("Profile.file") + document.name
                      : t("Profile.upload")}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ px: 4, py: 1.5, fontWeight: "bold" }}
              >
                {t("Profile.confrim")}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Profile;
